import { useState, useEffect } from "react";
import { Form, Field, FormElement } from "@progress/kendo-react-form";
import { Button } from "@progress/kendo-react-buttons";
import {
    FormInput,
    FormDropDownListFilter,
    FormNumericTextBox,
    FormDatePicker,
    FormSwitch,
    FormTextArea,
    FormAddressLookup,
} from "../FormComponents";
import { createGoNoGo } from "../../../data/goNoGoData";
import { searchAddresses, getFullAddress } from "../../../helpers/addressLookup";
import { getClientsDropdown } from "../../../data/clientData";
import { getAddressesForClientDropdown, getClientsForClientsDropdown } from "../../../data/accountsData";
import { dropDown } from "../../../data/interfaces";
import "../../../pages/GoNoGo/GoNoGo.css";

const AddGoNoGoForm = (props: any) => {
    const { onClose, lockEdit } = props;
    const [addresses, setAddresses] = useState<any[]>([]);
    const [saving, setSaving] = useState(false);

    // Mode: 'existing' (link client/site/contact) or 'manual' (free text + address lookup)
    const [mode, setMode] = useState<'existing' | 'manual'>('existing');

    // Existing mode state
    const [clientData, setClientData] = useState<dropDown[]>([]);
    const [siteData, setSiteData] = useState<dropDown[]>([]);
    const [contactData, setContactData] = useState<dropDown[]>([]);
    const [selectedClient, setSelectedClient] = useState<dropDown | null>(null);
    const [selectedSite, setSelectedSite] = useState<dropDown | null>(null);
    const [selectedContact, setSelectedContact] = useState<dropDown | null>(null);

    useEffect(() => {
        getClientsDropdown('').then(setClientData).catch(console.error);
    }, []);

    useEffect(() => {
        if (selectedClient && Number(selectedClient.value) > 0) {
            getAddressesForClientDropdown(Number(selectedClient.value)).then((data) => {
                setSiteData(data);
                setSelectedSite(data.length === 1 ? data[0] : null);
            }).catch(console.error);
            getClientsForClientsDropdown(Number(selectedClient.value)).then((data) => {
                setContactData(data);
                setSelectedContact(data.length === 1 ? data[0] : null);
            }).catch(console.error);
        } else {
            setSiteData([]);
            setContactData([]);
            setSelectedSite(null);
            setSelectedContact(null);
        }
    }, [selectedClient]);

    const searchClientDropdown = async (term: string) => {
        const data = await getClientsDropdown(term);
        setClientData(data);
    };

    const handleAddressSelect = (addressId: string, formRenderProps: any) => {
        getFullAddress(addressId).then((full: any) => {
            if (full) {
                formRenderProps.onChange("location", { value: full.suggestion || full.line1 || "" });
            }
        });
    };

    const handleSubmit = async (values: any) => {
        setSaving(true);
        try {
            await createGoNoGo({
                client: mode === 'manual' ? (values.client || null) : (selectedClient?.text || null),
                sector: values.sector || null,
                value: values.value ?? null,
                location: values.location || null,
                reference: values.reference || null,
                dueDate: values.dueDate ? new Date(values.dueDate).toISOString() : null,
                qualityPriceWeight: values.qualityPriceWeight || null,
                capacityToBid: values.capacityToBid ?? null,
                existingRelationship: values.existingRelationship ?? null,
                link: values.link || null,
                dateOnsite: values.dateOnsite ? new Date(values.dateOnsite).toISOString() : null,
                duration: values.duration || null,
                capacityToDeliver: values.capacityToDeliver ?? null,
                description: values.description || null,
                go: mode === 'existing' ? (values.go ?? false) : false,
                clientId: mode === 'existing' ? (selectedClient ? Number(selectedClient.value) : null) : null,
                siteId: mode === 'existing' ? (selectedSite ? Number(selectedSite.value) : null) : null,
                contactId: mode === 'existing' ? (selectedContact ? Number(selectedContact.value) : null) : null,
            });
            onClose();
        } catch (e) {
            console.error(e);
        } finally {
            setSaving(false);
        }
    };

    const canSetGo = mode === 'existing' && !!selectedClient && !!selectedSite;

    return (
        <div>
            {/* Mode toggle */}
            <div style={{ padding: "0 0 12px", display: "flex", gap: 8, alignItems: "center" }}>
                <span style={{ fontSize: "0.9em", fontWeight: 600 }}>Mode:</span>
                <button
                    type="button"
                    onClick={() => setMode('existing')}
                    style={{
                        padding: "4px 12px", borderRadius: 4, border: "1px solid #ccc", cursor: "pointer",
                        background: mode === 'existing' ? "#3498db" : "#fff",
                        color: mode === 'existing' ? "#fff" : "#333",
                        fontWeight: mode === 'existing' ? 600 : 400,
                    }}
                >
                    Existing Client/Site
                </button>
                <button
                    type="button"
                    onClick={() => setMode('manual')}
                    style={{
                        padding: "4px 12px", borderRadius: 4, border: "1px solid #ccc", cursor: "pointer",
                        background: mode === 'manual' ? "#3498db" : "#fff",
                        color: mode === 'manual' ? "#fff" : "#333",
                        fontWeight: mode === 'manual' ? 600 : 400,
                    }}
                >
                    Manual Entry
                </button>
            </div>

            <Form
                onSubmit={handleSubmit}
                render={(formRenderProps) => (
                    <FormElement style={{ flex: 1, display: "flex", flexDirection: "column" }}>
                        <div className="form-row">
                            <div className="form-field">
                                <Field name="reference" label="Reference" component={FormInput} />
                            </div>
                        </div>

                        {mode === 'existing' ? (
                            <>
                                <div className="form-row">
                                    <div className="form-field" style={{ flex: 1 }}>
                                        <FormDropDownListFilter
                                            data={clientData}
                                            originalData={clientData}
                                            value={selectedClient}
                                            name="clientDropdown"
                                            label="Client"
                                            setData={setClientData}
                                            searchDropdown={searchClientDropdown}
                                            onChange={(e: any) => {
                                                setSelectedClient(e.value);
                                                setSelectedSite(null);
                                                setSelectedContact(null);
                                            }}
                                        />
                                    </div>
                                </div>
                                <div className="form-row">
                                    <div className="form-field" style={{ flex: 1 }}>
                                        <FormDropDownListFilter
                                            data={siteData}
                                            originalData={siteData}
                                            value={selectedSite}
                                            name="siteDropdown"
                                            label="Site"
                                            setData={setSiteData}
                                            searchDropdown={() => {}}
                                            disabled={!selectedClient}
                                            onChange={(e: any) => setSelectedSite(e.value)}
                                        />
                                    </div>
                                </div>
                                <div className="form-row">
                                    <div className="form-field" style={{ flex: 1 }}>
                                        <FormDropDownListFilter
                                            data={contactData}
                                            originalData={contactData}
                                            value={selectedContact}
                                            name="contactDropdown"
                                            label="Contact"
                                            setData={setContactData}
                                            searchDropdown={() => {}}
                                            disabled={!selectedClient}
                                            onChange={(e: any) => setSelectedContact(e.value)}
                                        />
                                    </div>
                                </div>
                            </>
                        ) : (
                            <>
                                <div className="form-row">
                                    <div className="form-field">
                                        <Field name="client" label="Client (manual)" component={FormInput} />
                                    </div>
                                    <div className="form-field">
                                        <Field
                                            name="addressLookup"
                                            label="Location"
                                            component={FormAddressLookup}
                                            data={addresses}
                                            searchDropdown={(term: string) => searchAddresses(term, setAddresses)}
                                            onChange={(e: any) => {
                                                if (e.value?.id) handleAddressSelect(e.value.id, formRenderProps);
                                            }}
                                        />
                                    </div>
                                </div>
                            </>
                        )}

                        <div className="form-row">
                            <div className="form-field">
                                <Field name="sector" label="Sector" component={FormInput} />
                            </div>
                            <div className="form-field">
                                <Field name="value" label="Value (£)" component={FormNumericTextBox} format="n2" />
                            </div>
                        </div>
                        <div className="form-row">
                            <div className="form-field">
                                <Field name="dueDate" label="Due Date" component={FormDatePicker} />
                            </div>
                            <div className="form-field">
                                <Field name="qualityPriceWeight" label="Quality/Price Weight" component={FormInput} />
                            </div>
                        </div>
                        <div className="form-row">
                            <div className="form-field">
                                <Field name="duration" label="Duration" component={FormInput} />
                            </div>
                            <div className="form-field">
                                <Field name="dateOnsite" label="Date Onsite" component={FormDatePicker} />
                            </div>
                        </div>
                        <div className="form-row">
                            <div className="form-field">
                                <Field name="link" label="Link" component={FormInput} />
                            </div>
                        </div>
                        <div className="form-row">
                            <div className="form-field">
                                <Field name="capacityToBid" label="Capacity to Bid?" component={FormSwitch} />
                            </div>
                            <div className="form-field">
                                <Field name="existingRelationship" label="Existing Relationship?" component={FormSwitch} />
                            </div>
                            <div className="form-field">
                                <Field name="capacityToDeliver" label="Capacity to Deliver?" component={FormSwitch} />
                            </div>
                        </div>
                        <div className="form-row" style={{ flex: 1 }}>
                            <div className="form-field">
                                <Field name="description" label="Description" component={FormTextArea} rows={4} />
                            </div>
                        </div>
                        <div className="form-row">
                            <div className="form-field">
                                <Field name="go" label="Go?" component={FormSwitch} disabled={!canSetGo} />
                                {!canSetGo && (
                                    <span style={{ fontSize: "0.75em", color: "#e74c3c" }}>
                                        Requires existing client &amp; site
                                    </span>
                                )}
                            </div>
                        </div>
                        <div className="form-actions">
                            <Button
                                type="submit"
                                themeColor="primary"
                                disabled={saving || lockEdit || !formRenderProps.allowSubmit}
                            >
                                {saving ? "Saving..." : "Save"}
                            </Button>
                        </div>
                    </FormElement>
                )}
            />
        </div>
    );
};

export default AddGoNoGoForm;
